/**
 * useEditorData — Thin adapter for data loading and orchestration.
 * Phase 3B: delegates tree state to TanStack Query (useScenarioTree),
 * section loading to useSectionLoader, and removes reloadState/mounted.
 *
 * Remaining responsibilities:
 * - Scenario/sensitivity initialization from API
 * - Sensitivity overlay application
 * - Excel sheet refresh stubs (Office.js API calls)
 * - Coordination between query hooks
 */

import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import apiService from '../../../services/ApiService';
import { useEditorStore } from '../../../shared/stores/useEditorStore';
import { ModelSection } from '../../../types/ModelSection';
import { buildSectionsFromSummaries } from '../../../utils/modelDataMapper';
import {
    mapApiResponse,
    copyFields,
    setExpandedRef,
} from '../services/dataMappers';
import {
    SCENARIO_TREE_KEY,
    type EditorState,
    createInitialState,
    patchTreeCache,
} from '../../../shared/hooks/queries/useScenarioTree';
import { getSensitivity, getScenarioSections, getComputeErrors, getSensitivityComputeErrors } from '../../../shared/api/scenarios';

interface UseEditorDataParams {
    currentScenario: any;
    currentSensitivity: any;
    currentSection: ModelSection | null;
    viewMode: 'overview' | 'section';
    sendJsonMessage: (msg: any, force?: boolean) => void;
    computeFailedRef: React.MutableRefObject<boolean>;
    setComputeFailed: (v: boolean) => void;
    setCurrentSensitivity: React.Dispatch<any>;
    navigateToPage: (page: any) => void;
    SubPages: any;
    loadSection: (sectionId: string, forceReload?: boolean) => Promise<void>;
    resetLoadedSections: () => void;
}

export function useEditorData({
    currentScenario,
    currentSensitivity,
    currentSection,
    viewMode,
    sendJsonMessage,
    computeFailedRef,
    setComputeFailed,
    setCurrentSensitivity,
    navigateToPage,
    SubPages,
    loadSection,
    resetLoadedSections,
}: UseEditorDataParams) {
    const queryClient = useQueryClient();

    const [dataLoading, setDataLoading] = useState(false);
    const [downloadButtonAvailable, setDownloadButtonAvailable] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [sections, setSections] = useState<ModelSection[]>([]);
    const [computeErrors, setComputeErrors] = useState<any[]>([]);
    const [sensitivityModifiedIds, setSensitivityModifiedIds] = useState<Set<string>>(new Set());

    const scenarioId = currentScenario?.id ?? 0;

    const patchTree = useCallback(
        (updater: (prev: EditorState) => EditorState) =>
            patchTreeCache(queryClient, scenarioId, updater),
        [queryClient, scenarioId]
    );

    // Excel refresh stubs (Office.js integration disabled)
    const recreateExcelInputIfSync = useCallback(async () => {}, []);
    const refreshExcelTimeSerieSheet = useCallback(async () => {}, []);
    const refreshExcelCompanyAssetSheet = useCallback(async () => {}, []);
    const refreshExcelInputSheet = useCallback(async () => {}, []);

    // Sensitivity overlay
    const applySensitivityOverlay = useCallback((sensitivity: any) => {
        patchTree((prev) => {
            const newInputItems = { ...prev.Input.tree.items };
            Object.keys(newInputItems).forEach((key) => {
                newInputItems[key] = { ...newInputItems[key], sensitivityModelData: null };
            });

            sensitivity.model_datas?.forEach((sObj: any) => {
                const item = newInputItems[sObj.original_model_data_id];
                if (item) {
                    newInputItems[sObj.original_model_data_id] = {
                        ...item,
                        sensitivityModelData: sObj,
                    };
                }
            });

            if (sensitivity.modified_model_data_ids) {
                setSensitivityModifiedIds(new Set(sensitivity.modified_model_data_ids.map((id: number) => id.toString())));
            }

            // Clear output fields for sensitivity
            const newOutputItems = { ...prev.Output.tree.items };
            Object.keys(newOutputItems).forEach((key) => {
                const obj = newOutputItems[key];
                if (obj.fields) {
                    const newFields = { ...obj.fields };
                    Object.keys(newFields).forEach((fKey) => {
                        const field = newFields[fKey];
                        if (field.type === 'float') {
                            newFields[fKey] = { ...field, value: { ...field.value, value: null }, compute_error: null };
                        } else if (field.type === 'time_serie') {
                            newFields[fKey] = { ...field, value: { ...field.value, value: { ...field.value.value, data: [] } }, compute_error: null };
                        } else if (field.type === 'grid' || field.type === 'dynamic_grid') {
                            newFields[fKey] = { ...field, value: { ...field.value, value: [] }, compute_error: null };
                        } else {
                            newFields[fKey] = { ...field, compute_error: null };
                        }
                    });
                    newOutputItems[key] = { ...obj, compute_error: null, fields: newFields };
                }
            });

            // Clear input errors
            Object.keys(newInputItems).forEach((key) => {
                const obj = newInputItems[key];
                if (obj.fields) {
                    const newFields = { ...obj.fields };
                    Object.keys(newFields).forEach((fKey) => {
                        newFields[fKey] = { ...newFields[fKey], compute_error: null };
                    });
                    newInputItems[key] = { ...obj, compute_error: null, fields: newFields };
                }
            });

            // Apply sensitivity output
            if (sensitivity.output) {
                sensitivity.output.forEach((obj: any) => {
                    const currentObj = newOutputItems[obj.id.toString()];
                    if (currentObj) {
                        const patched = { ...currentObj };
                        copyFields(obj, patched);
                        newOutputItems[obj.id.toString()] = patched;
                    }
                });
            }

            if (currentScenario?.sensitivities) {
                currentScenario.sensitivities.forEach((s: any) => {
                    if (s.id == sensitivity.id) {
                        s.dashboard_computed = sensitivity.dashboard_computed;
                        s.is_computed = sensitivity.is_computed;
                    }
                });
            }

            return {
                ...prev,
                Input: { ...prev.Input, tree: { ...prev.Input.tree, items: newInputItems } },
                Output: { ...prev.Output, tree: { ...prev.Output.tree, items: newOutputItems } },
            };
        });
    }, [patchTree, currentScenario]);

    // Load scenario sections from API
    const loadScenarioFromApi = useCallback(async () => {
        if (!currentScenario?.id) return;
        computeFailedRef.current = false;
        setComputeFailed(false);
        setDownloadButtonAvailable(false);
        setDataLoading(true);

        // Reset tree state and loaded sections tracking
        queryClient.setQueryData<EditorState>(
            [SCENARIO_TREE_KEY, scenarioId],
            createInitialState()
        );
        resetLoadedSections();

        try {
            const responseData = await getScenarioSections(currentScenario.id);

            const sectionsList = buildSectionsFromSummaries(responseData.sections || []);
            setSections(sectionsList);
            const loadedErrors = responseData.compute_errors || [];
            setComputeErrors(loadedErrors);
            if (loadedErrors.length > 0) {
                computeFailedRef.current = true;
                setComputeFailed(true);
            }
            setDownloadButtonAvailable(true);

            if (currentSensitivity === 'null') {
                sendJsonMessage({ 'type': 'scenario_state_request' });
            } else {
                sendJsonMessage({ 'type': 'sensitivity_state_request', 'sensitivity_id': currentSensitivity.id });
            }

            setCurrentSensitivity((prev: any) => {
                if (prev && prev !== 'null' && typeof prev === 'object') {
                    return { ...prev };
                }
                return prev;
            });

            if (viewMode === 'section' && currentSection) {
                await loadSection(currentSection.config.id, true);
            }
        } catch (error) {
            console.error('Error loading scenario sections:', error);
            navigateToPage(SubPages.MyModels);
        }
        setDataLoading(false);
    }, [queryClient, scenarioId, currentScenario, currentSensitivity, viewMode, currentSection, sendJsonMessage, loadSection, resetLoadedSections, navigateToPage, SubPages, computeFailedRef, setComputeFailed, setCurrentSensitivity]);

    // Load sensitivity overlay from API
    const loadSensitivityFromApi = useCallback(async () => {
        if (currentSensitivity !== 'null') {
            setDownloadButtonAvailable(false);
            setDataLoading(true);

            try {
                // Fetch sensitivity data + errors in parallel (no pre-clear to avoid flash)
                const [sensitivity, errors] = await Promise.all([
                    getSensitivity(currentSensitivity.id),
                    getSensitivityComputeErrors(currentSensitivity.id).catch(() => []),
                ]);

                applySensitivityOverlay(sensitivity);
                currentSensitivity.is_computed = sensitivity.is_computed;

                // Set errors atomically — no intermediate empty state
                setComputeErrors(errors);
                if (errors.length > 0) {
                    computeFailedRef.current = true;
                    setComputeFailed(true);
                } else {
                    computeFailedRef.current = false;
                    setComputeFailed(false);
                }

                if (currentSection) {
                    await loadSection(currentSection.config.id, true);
                }

                const currentState = queryClient.getQueryData<EditorState>([SCENARIO_TREE_KEY, scenarioId]);
                if (currentState) {
                    setExpandedRef(currentState);
                }
                setDownloadButtonAvailable(true);
            } catch (error) {
                console.error('Error loading sensitivity:', error);
            }
            setDataLoading(false);
            setInitialized(true);
        } else {
            setSensitivityModifiedIds(new Set());
            useEditorStore.getState().clearExpandedNodes();
            queryClient.setQueryData<EditorState>(
                [SCENARIO_TREE_KEY, scenarioId],
                createInitialState()
            );
            resetLoadedSections();
            if (initialized) {
                sendJsonMessage({ 'type': 'scenario_state_request' });
                if (currentSection) {
                    await loadSection(currentSection.config.id, true);
                }
                // Fetch scenario compute errors when switching back from sensitivity
                const errors = await getComputeErrors(scenarioId).catch(() => []);
                setComputeErrors(errors);
                if (errors.length > 0) {
                    computeFailedRef.current = true;
                    setComputeFailed(true);
                } else {
                    computeFailedRef.current = false;
                    setComputeFailed(false);
                }
            } else {
                setInitialized(true);
            }
        }
    }, [queryClient, scenarioId, currentSensitivity, currentSection, initialized, loadScenarioFromApi, loadSection, resetLoadedSections, sendJsonMessage, applySensitivityOverlay, computeFailedRef, setComputeFailed]);

    // Reload model data (used by ExcelBridge and external callers)
    const reloadModelDatas = useCallback(async (editingId?: string) => {
        const currentState = queryClient.getQueryData<EditorState>([SCENARIO_TREE_KEY, scenarioId]);
        if (currentState) {
            setExpandedRef(currentState);
        }
        if (currentSection) {
            await loadSection(currentSection.config.id, true);
            recreateExcelInputIfSync();
        } else {
            try {
                const response = await apiService.getModelDataByScenario(currentScenario.id);
                if (response) {
                    const data = await response.json();
                    const newState = mapApiResponse(data, editingId);
                    queryClient.setQueryData<EditorState>(
                        [SCENARIO_TREE_KEY, scenarioId],
                        newState as EditorState
                    );
                    recreateExcelInputIfSync();
                }
            } catch (error) {
                console.error('Error reloading model data:', error);
            }
        }
    }, [queryClient, scenarioId, currentSection, currentScenario, loadSection, recreateExcelInputIfSync]);

    // Reload sensitivity
    const reloadSensitivity = useCallback(async () => {
        const currentState = queryClient.getQueryData<EditorState>([SCENARIO_TREE_KEY, scenarioId]);
        if (currentState) {
            setExpandedRef(currentState);
        }
        try {
            const sensitivity = await getSensitivity(currentSensitivity.id);

            patchTree((prev) => {
                const newInputItems = { ...prev.Input.tree.items };
                sensitivity.model_datas?.forEach((sObj: any) => {
                    const item = newInputItems[sObj.original_model_data_id];
                    if (item) {
                        newInputItems[sObj.original_model_data_id] = {
                            ...item,
                            sensitivityModelData: sObj,
                        };
                    }
                });
                currentSensitivity.is_computed = sensitivity.is_computed;
                currentSensitivity.dashboard_computed = sensitivity.dashboard_computed;
                return {
                    ...prev,
                    Input: { ...prev.Input, tree: { ...prev.Input.tree, items: newInputItems } },
                };
            });
        } catch (error) {
            console.error('Error reloading sensitivity:', error);
        }
    }, [queryClient, scenarioId, currentSensitivity, patchTree]);

    return {
        dataLoading,
        downloadButtonAvailable,
        sections,
        setSections,
        computeErrors,
        setComputeErrors,
        sensitivityModifiedIds,
        recreateExcelInputIfSync,
        refreshExcelTimeSerieSheet,
        refreshExcelCompanyAssetSheet,
        refreshExcelInputSheet,
        loadScenarioFromApi,
        loadSensitivityFromApi,
        reloadModelDatas,
        reloadSensitivity,
    };
}
